import { Elements } from '@/components/elements';
import DoodleLine from '@icons/DoodleLine.svg';
import clsx from 'clsx';
import Image from 'next/image';

export type SwitchingToPBSectionCard = {
    iconSrc: string;
    iconAlt?: string;
    title: string;
    description: string;
    /** Optional bullet list rendered below the description. */
    listItems?: string[];
};

export type SwitchingToPBSectionProps = {
    cards: SwitchingToPBSectionCard[];
    className?: string;
};


export function SwitchingToPBSection({
    cards,
    className,
}: SwitchingToPBSectionProps) {
    return (
        <section
            className={clsx(
                'overflow-hidden px-5 py-16 md:px-10 md:py-16 lg:px-[79px] lg:py-20',
                className,
            )}
        >
            <div
                className={clsx(
                    'relative mx-auto w-full max-w-[1280px] overflow-hidden rounded-[40px] bg-brand-900',
                    'p-4 md:px-10 md:py-16 lg:p-16',
                )}
            >
                <Image
                    src={DoodleLine}
                    alt=""
                    width={1440}
                    height={838}
                    className={clsx(
                        'pointer-events-none select-none',
                        'absolute bottom-[-60%] left-[-10%] z-0 max-h-none max-w-none',
                        'h-[838px] w-[1440px]',
                        'rounded-bl-[40px]',
                        'md:bottom-[-5%] md:left-[85%] md:-translate-x-1/2',
                        'lg:bottom-[-60%] lg:left-[-10%] lg:translate-x-0',
                    )}
                    aria-hidden
                />
                <div className="relative z-10 flex flex-col gap-8">
                <div className="flex flex-col items-center gap-4 text-center">
                    <span
                        className={clsx(
                            'inline-flex rounded-lg bg-brand-100 px-2 py-1 text-base font-medium tracking-[0.12em] text-brand-700 uppercase',
                        )}
                    >
                        SOLUTION
                    </span>
                    <Elements.Title
                        title='Switching to Predictable Benefits (PB)'
                        type="h2"
                        className="max-w-[920px] text-3xl font-semibold text-gray-50 md:text-[32px] lg:text-4xl"
                    />
                    <Elements.Description
                        description='The brokerage firm bypassed the traditional vendors and implemented the Predictable Benefits White-Labeled SaaS:'
                        type="medium"
                        className="text-balance text-gray-50 md:text-lg"
                    />
                </div>
                <div className="grid w-full grid-cols-1 gap-4 md:gap-5 lg:grid-cols-3 lg:gap-6">
                    {cards.map((card, index) => (
                        <div
                            key={`${card.title}-${index}`}
                            className="relative flex min-h-0 h-full w-full flex-col rounded-2xl"
                        >
                            <div
                                aria-hidden
                                className="pointer-events-none absolute inset-0 rounded-2xl"
                                style={{
                                    padding: 1,
                                    borderRadius: '1rem',
                                    background:
                                        'linear-gradient(135deg, rgba(255,255,255,0.42) 0%, rgba(255,255,255,0.06) 42%, rgba(133,191,248,0.5) 100%)',
                                    WebkitMask:
                                        'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                    WebkitMaskComposite: 'xor',
                                    mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                    maskComposite: 'exclude',
                                }}
                            />
                            <div
                                className={clsx(
                                    'relative z-10 flex min-h-0 h-full w-full flex-1 flex-col gap-3 rounded-2xl bg-[#0077DF1F] p-4',
                                    'shadow-[inset_0_1px_0_0_rgba(255,255,255,0.18)]',
                                )}
                            >
                                <div className="flex w-fit items-center gap-4 rounded-full bg-[#FFFFFF1A] px-3 py-2">
                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-50">
                                        <Image
                                            src={card.iconSrc}
                                            alt={card.iconAlt ?? ''}
                                            width={20}
                                            height={20}
                                            className="h-5 w-5 object-contain"
                                        />
                                    </div>
                                    <Elements.Title
                                        title={card.title}
                                        type="h5"
                                        className="min-w-0 flex-1 text-left text-lg! font-semibold text-gray-50 md:text-xl!"
                                    />
                                </div>
                                <Elements.Description
                                    description={card.description}
                                    type="small"
                                    className="w-full text-left text-gray-50 md:text-lg"
                                />
                                {card.listItems?.length ? (
                                    <ul
                                        className={clsx(
                                            'w-full list-disc space-y-1.5 pl-5 text-left text-sm leading-snug text-gray-50',
                                            'marker:text-gray-300',
                                        )}
                                    >
                                        {card.listItems.map((item, itemIndex) => (
                                            <li
                                                key={`${card.title}-li-${itemIndex}`}
                                                className="text-base text-gray-50"
                                            >
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                ) : null}
                            </div>
                        </div>
                    ))}
                </div>
                </div>
            </div>
        </section>
    );
}
